import { type ColumnDef } from "@tanstack/react-table";

// Convert camelCase to Title Case with spaces
const formatColumnHeader = (key: string): string => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Generate dynamic columns based on the data
export const generateDynamicColumns = (
  data: Record<string, any>[]
): ColumnDef<Record<string, any>>[] => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  // Collect all unique keys from all objects
  const allKeys = new Set<string>();
  data.forEach((item) => {
    Object.keys(item).forEach((key) => allKeys.add(key));
  });

  const columns: ColumnDef<Record<string, any>>[] = [];

  allKeys.forEach((key) => {
    // Skip claimId column
    if (key === "claimId") {
      return;
    }

    // Special handling for billingDetails
    if (key === "billingDetails") {
      columns.push({
        accessorKey: "billingDetails",
        header: "Billing Details",
        cell: ({ row }) => {
          const details = row.getValue("billingDetails") as Record<string, any>;
          if (!details || typeof details !== "object") return <div>—</div>;

          // Filter out null/empty values and format as bullet points
          const entries = Object.entries(details).filter(
            ([_, value]) => value !== null && value !== "" && value !== undefined
          );

          if (entries.length === 0) return <div>—</div>;

          return (
            <ul className="text-xs space-y-1 list-disc list-inside">
              {entries.map(([key, value], index) => {
                // Format the key to be more readable
                const formattedKey = key
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())
                  .trim();

                return (
                  <li key={index} className="break-words">
                    <strong>{formattedKey}:</strong> {String(value)}
                  </li>
                );
              })}
            </ul>
          );
        },
        enableResizing: true,
        enableSorting: false,
      });
    } else if (key === "codesExplanation") {
      columns.push({
        accessorKey: "codesExplanation",
        header: "Codes Explanation",
        cell: ({ row }) => {
          const codes = row.getValue("codesExplanation") as Array<{
            codeType: string;
            codeValue: string;
            description: string;
          }>;
          if (!codes || codes.length === 0) return <div>—</div>;
          return (
            <div className="break-words overflow-hidden">
              {codes.map((code, index) => (
                <div key={index} className="text-xs mb-1 break-words">
                  {code.codeType} {code.codeValue} {code.description || ""}
                </div>
              ))}
            </div>
          );
        },
        enableResizing: true,
        enableSorting: false,
      });
    } else {
      // Standard column generation
      const header = formatColumnHeader(key);

      columns.push({
        accessorKey: key,
        header: header,
        cell: ({ row }) => {
          const value = row.getValue(key);

          // Handle missing values
          if (value === undefined || value === null || value === "") {
            return <div>—</div>;
          }

          // Special formatting for specific columns
          if (key === "memberId" || key === "claimNumber") {
            return (
              <div className="text-xs max-w-[50rem] overflow-x-auto whitespace-nowrap">
                {String(value)}
              </div>
            );
          }

          if (key === "billedAmount" || key === "allowedAmount") {
            return <div className="text-right text-xs">{`$${value}`}</div>;
          }

          if (key === "updatedAt") {
            try {
              const date = new Date(String(value));
              const formatted = new Intl.DateTimeFormat("en-US", {
                timeZone: "America/New_York",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
              }).format(date);
              return <div className="text-xs break-words overflow-hidden">{formatted}</div>;
            } catch (error) {
              return <div className="text-xs break-words overflow-hidden">{String(value)}</div>;
            }
          }

          if (key === "claimStatus") {
            const status = String(value).toLowerCase();
            let bgColor = "bg-gray-100";
            let textColor = "text-gray-800";

            if (status === "paid" || status === "adjusted" || status === "finalized") {
              bgColor = "bg-green-100";
              textColor = "text-green-800";
            } else if (status === "denied") {
              bgColor = "bg-red-100";
              textColor = "text-red-800";
            } else if (status === "pending") {
              bgColor = "bg-yellow-100";
              textColor = "text-yellow-800";
            }

            return (
              <div
                className={`inline-flex items-center px-2.5 py-0.5 rounded-none text-xs font-medium ${bgColor} ${textColor}`}
              >
                {String(value)}
              </div>
            );
          }

          if (key === "notesAndComments") {
            return <div className="break-words overflow-hidden">{String(value) || "—"}</div>;
          }

          // Default cell rendering
          return <div>{String(value)}</div>;
        },
        enableResizing: true,
      });
    }
  });

  // Make the last 3 columns 1.5x wider (225px instead of default 150px)
  const lastThreeStartIndex = Math.max(0, columns.length - 3);
  columns.forEach((column, index) => {
    if (index >= lastThreeStartIndex) {
      (column as any).size = 225; // 1.5x the default 150px
    }
  });

  return columns;
};
